import { t } from '@lingui/macro';
import { every, get, isEmpty, some } from 'lodash';
import { AGENT_RESULT_STATUS, QUICK_TEST_TYPE, WIFI_WIRELESS_ADAPTER_TYPES } from './VerifySharedConstants';
import { createAgentResultObject, getVerifyAgents, processOneResult } from './VerifyStrategyHelpers';
import { WIRELESS } from '../../ServiceTestsConstants';

const POLL_INTERVAL_MS = 2000;
const POLL_COUNT_MAX = 900; // seconds

/**
 * Build the scripted/WiFi/BTT verify payload.
 * portal/ui source: verify.service.js buildScriptedVerifyTest()
 *
 * @param {Object} formData
 * @param {Object} testType
 * @param {Array} agents
 * @returns {Object} Payload for POST to quicktests API
 */
function buildVerifyPayload(formData, testType, agents) {
    const agentIds = getVerifyAgents(agents, formData, testType);

    // Force wireless adapter for WiFi test types (portal/ui: BASE_SCRIPTED_TEST.WIFI_CONNECT, WIFI)
    let { adapterType } = formData;
    if (WIFI_WIRELESS_ADAPTER_TYPES.has(testType.name)) {
        adapterType = WIRELESS;
    }

    const payload = {
        name: `Verify ${formData.name || ''}`,
        type: testType._id,
        quickTestType: QUICK_TEST_TYPE.OTHER,
        beaconPackets: get(formData, 'beaconPackets', false),
        adapterType,
        testConfig: formData.testConfig,
        agents: { ids: agentIds },
        agentResults: agentIds.map(createAgentResultObject),
    };

    // BTT auth data (from verify.service.js:687-689)
    if (testType.bttTest || testType.showBttAuth) {
        payload.bttUser = get(formData, 'bttUser', {});
    }

    return payload;
}

/**
 * Check the status of a scripted verify test.
 * portal/ui source: verifyScriptedTest.service.js checkStatus()
 *
 * Key difference from Web: uses some() — ANY single agent succeeding means overall success.
 * Success = agent status 'C' AND results.length > 0 AND every result has scriptAvailability === 1 AND availability === 1.
 *
 * @param {Object} response - Quick test response from API
 * @returns {{ isSuccessful: boolean, stillRunning: boolean }}
 */
function checkStatus(response) {
    const agentResults = response.agentResults || [];

    const isSuccessful = some(
        agentResults,
        (agentResult) =>
            agentResult.status === AGENT_RESULT_STATUS.COMPLETE &&
            agentResult.results?.length > 0 &&
            every(
                agentResult.results,
                (result) => get(result, 'data.scriptAvailability') === 1 && get(result, 'data.availability') === 1
            )
    );

    const stillRunning = some(
        agentResults,
        (agentResult) => agentResult.status !== AGENT_RESULT_STATUS.COMPLETE && (agentResult.results?.length || 0) === 0
    );

    return { isSuccessful, stillRunning };
}

/**
 * Check if scripted response has any results.
 * portal/ui source: verifyScriptedTest.service.js verifyHasResults()
 *
 * @param {Object} response
 * @returns {boolean}
 */
function verifyHasResults(response) {
    return some(response.agentResults, (agentResult) => !isEmpty(agentResult.results));
}

/**
 * Build the verify result object for scripted tests.
 * portal/ui source: verifyScriptedTest.service.js buildVerifyResults()
 *
 * @param {boolean} isSuccessful
 * @param {Object} response - Full quick test response
 * @param {Function} getAgentName - (agentId) => displayName
 * @returns {Object} Verify result { status, statusMsg, resultType, resultData }
 */
function buildVerifyResult(isSuccessful, response, getAgentName) {
    const statusLabel = isSuccessful ? t`Success` : t`Failure`;
    let statusMsg = isSuccessful
        ? t`The test executed successfully`
        : t`The test configuration doesn't seem to be valid, some nPoints have returned failure. Check the test configuration and try again.`;

    // Special error handling: "Verify Not Started" (from verifyScriptedTest.service.js:46-50)
    if (!isSuccessful && verifyHasResults(response)) {
        const errorString = get(response, 'agentResults[0].results[0].data.errorCodeString', '');
        if (errorString === 'Verify Not Started') {
            statusMsg = t`A verify test is already running. Please wait for a few moments and try again.`;
        }
    }

    // Build structured result data for each agent
    const agentRows = (response.agentResults || []).map((agentResult) => {
        const agentName = getAgentName(agentResult.agent);
        const results = (agentResult.results || []).map(processOneResult);
        return { agentName, results };
    });

    return {
        status: statusLabel,
        statusMsg,
        resultType: 'scripted',
        resultData: { agentRows, isSuccessful },
    };
}

/**
 * Dynamic polling counter for scripted tests.
 * portal/ui source: verifyScriptedTest.service.js pollingCounter()
 *
 * For WiFi Connect tests, extends timeout to POLL_COUNT_MAX (900s).
 *
 * @param {Object} pollState - Mutable polling state
 * @returns {Object} Updated pollState
 */
function pollingCounter(pollState) {
    const agentResults = get(pollState, 'response.agentResults', []);
    const isComplete = some(agentResults, (ar) => ar.status === AGENT_RESULT_STATUS.COMPLETE && ar.results?.length > 0);
    const hasStarted = some(
        agentResults,
        (ar) => ar.status === AGENT_RESULT_STATUS.SETTING_UP && (ar.results?.length || 0) === 0
    );
    const defaultPollCount = (pollState.testTypeTimeout * 1000) / POLL_INTERVAL_MS + 10;

    if (isComplete) {
        // eslint-disable-next-line no-param-reassign
        pollState.pollFor = pollState.pollCount;
    } else if (hasStarted) {
        if (!pollState.hasStarted) {
            // eslint-disable-next-line no-param-reassign
            pollState.hasStarted = true;
            // eslint-disable-next-line no-param-reassign
            pollState.pollFor = pollState.pollCount + defaultPollCount;
        }
    } else {
        const maxPollCount = (POLL_COUNT_MAX * 1000) / POLL_INTERVAL_MS + 10;
        // eslint-disable-next-line no-param-reassign
        pollState.pollFor = pollState.testType?.wifiConnectTest ? maxPollCount : defaultPollCount;
    }

    return pollState;
}

// Strategy for scripted (non-Web, non-VoIP) test types. To add a new test type strategy:
// 1. Create a new VerifyXxxStrategy.js implementing the VerifyStrategy interface
// 2. Register it in ServiceTestVerifyHelper.resolveStrategy()

/**
 * Verify strategy for scripted, WiFi, and BTT test types.
 * This is the default strategy for any test type not explicitly handled by Web or VoIP strategies.
 *
 * @type {import('./ServiceTestVerifyHelper').VerifyStrategy}
 */
const verifyScriptedStrategy = {
    buildVerifyPayload,
    checkStatus,
    verifyHasResults,
    buildVerifyResult,
    pollConfig: {
        pollFor: 45,
        pollIntervalMs: POLL_INTERVAL_MS,
    },
    pollingCounter,
};

export default verifyScriptedStrategy;
